import React, { useState } from 'react';
import {
  ArrowLeft,
  Clock,
  TrendingUp,
  Info,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  User,
  EyeOff,
  Edit2,
  Trash2,
  Hash,
  Server,
} from 'lucide-react';
import { format, parseISO, isPast } from 'date-fns';
import { Card, Button, StatusBadge, ConfirmModal } from '../ui';
import { useUIStore, useAuthStore, usePredictionStore } from '../../store';
import { api } from '../../lib/api';
import type { Prediction } from '../../types';

interface PredictionDetailProps {
  prediction: Prediction;
}

export function PredictionDetail({ prediction }: PredictionDetailProps) {
  const { setView, showToast } = useUIStore();
  const { user } = useAuthStore();
  const { setSelectedPrediction } = usePredictionStore();

  const [isVoting, setIsVoting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isAuthor = user?.id === prediction.authorId;
  const isExpired = prediction.deadline && isPast(parseISO(prediction.deadline));
  const isInProgress = prediction.status === 'in-progress';
  const totalVotes = prediction.votes.success.length + prediction.votes.fail.length + prediction.votes.invalid.length;

  const userVote = user
    ? prediction.votes.success.includes(user.id)
      ? 'success'
      : prediction.votes.fail.includes(user.id)
      ? 'fail'
      : prediction.votes.invalid.includes(user.id)
      ? 'invalid'
      : null
    : null;

  const handleVote = async (type: 'success' | 'fail' | 'invalid') => {
    if (!user || !isInProgress) return;

    setIsVoting(true);
    try {
      const response = await api.predictions.vote(prediction.id, type);
      if (response.success) {
        showToast('Vote recorded', 'success');
      } else {
        showToast(response.error || 'Failed to vote', 'error');
      }
    } catch (error) {
      console.error('Vote failed:', error);
      showToast('Failed to vote', 'error');
    } finally {
      setIsVoting(false);
    }
  };

  const handleResolve = async (status: 'success' | 'fail' | 'invalidated') => {
    if (!user || !isAuthor) return;

    setIsResolving(true);
    try {
      const response = await api.predictions.resolve(prediction.id, status);
      if (response.success) {
        showToast('Prediction resolved', 'success');
        setView('feed');
      } else {
        showToast(response.error || 'Failed to resolve prediction', 'error');
      }
    } catch (error) {
      console.error('Resolve failed:', error);
      showToast('Failed to resolve prediction', 'error');
    } finally {
      setIsResolving(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !isAuthor) return;

    try {
      const response = await api.predictions.delete(prediction.id);
      if (response.success) {
        showToast('Prediction deleted', 'success');
        setSelectedPrediction(null);
        setView('feed');
      } else {
        showToast(response.error || 'Failed to delete prediction', 'error');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('Failed to delete prediction', 'error');
    }
  };

  const goBack = () => {
    setSelectedPrediction(null);
    setView('feed');
  };

  return (
    <div className="animate-in">
      <button
        onClick={goBack}
        className="text-slate-400 hover:text-white flex items-center gap-2 text-xs font-bold mb-8 transition-colors tracking-widest uppercase"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <Card padding="lg" className="rounded-[2rem] relative overflow-hidden">
        {isAuthor && (
          <div className="absolute top-0 right-0 px-4 py-2 bg-foretell-accent text-[10px] font-bold uppercase tracking-widest rounded-bl-xl">
            Your Prediction
          </div>
        )}

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <StatusBadge status={prediction.status} />
            {isExpired && isInProgress && (
              <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                Awaiting Resolution
              </span>
            )}
          </div>

          {prediction.deadline && (
            <div className="text-foretell-accent text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
              <Clock size={14} />
              Deadline: {format(parseISO(prediction.deadline), 'MMMM d, yyyy')}
            </div>
          )}

          <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-6 italic">
            "{prediction.text}"
          </h2>

          <div className="flex items-center gap-3 text-sm text-slate-400">
            {prediction.isAnonymous ? (
              <>
                <EyeOff size={16} />
                <span>Anonymous</span>
              </>
            ) : (
              <>
                {prediction.authorAvatar ? (
                  <img
                    src={`https://cdn.discordapp.com/avatars/${prediction.authorId.replace('discord_', '')}/${prediction.authorAvatar}.png?size=24`}
                    alt=""
                    className="w-6 h-6 rounded-full"
                  />
                ) : (
                  <User size={16} />
                )}
                <span>@{prediction.authorName}</span>
              </>
            )}
          </div>

          {/* Channel/Server Info */}
          <div className="mt-4 p-3 bg-black/30 rounded-xl border border-white/5">
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <Server size={14} />
                <span className="font-mono">{prediction.serverId}</span>
              </div>
              <div className="flex items-center gap-2">
                <Hash size={14} />
                <span className="font-mono">{prediction.channelId}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="p-4 bg-black/40 border border-white/10 rounded-2xl text-center">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              Confidence
            </div>
            <div className="text-2xl font-bold text-foretell-accent">
              {prediction.confidence}%
            </div>
          </div>
          <div className="p-4 bg-black/40 border border-white/10 rounded-2xl text-center">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              Total Votes
            </div>
            <div className="text-2xl font-bold">{totalVotes}</div>
          </div>
        </div>

        {prediction.invalidationThesis && (
          <div className="mb-8 p-4 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
            <div className="text-[10px] font-bold text-foretell-accent uppercase tracking-widest mb-2 flex items-center gap-2">
              <Info size={14} /> Invalidation Clause
            </div>
            <p className="text-slate-300 italic">"{prediction.invalidationThesis}"</p>
          </div>
        )}

        {isInProgress && user && (
          <div className="space-y-4 mb-8">
            <h3 className="text-[10px] font-bold text-white/30 uppercase tracking-widest">
              Cast Your Vote
            </h3>
            <div className="flex gap-3">
              <button
                onClick={() => handleVote('success')}
                disabled={isVoting}
                className={`flex-1 py-6 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${
                  userVote === 'success'
                    ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                    : 'bg-black/40 border-white/10 text-white/30 hover:border-white/30 hover:text-white/60'
                }`}
              >
                <CheckCircle2 size={32} />
                <span className="font-bold text-xs uppercase tracking-wider">Success</span>
                <span className="text-xs opacity-60">{prediction.votes.success.length} votes</span>
              </button>
              <button
                onClick={() => handleVote('fail')}
                disabled={isVoting}
                className={`flex-1 py-6 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${
                  userVote === 'fail'
                    ? 'bg-rose-500/20 border-rose-500 text-rose-400'
                    : 'bg-black/40 border-white/10 text-white/30 hover:border-white/30 hover:text-white/60'
                }`}
              >
                <XCircle size={32} />
                <span className="font-bold text-xs uppercase tracking-wider">Failed</span>
                <span className="text-xs opacity-60">{prediction.votes.fail.length} votes</span>
              </button>
            </div>
            {prediction.invalidationThesis && (
              <button
                onClick={() => handleVote('invalid')}
                disabled={isVoting}
                className={`w-full py-4 rounded-2xl border-2 flex items-center justify-center gap-2 transition-all ${
                  userVote === 'invalid'
                    ? 'bg-slate-500/20 border-slate-500 text-slate-400'
                    : 'bg-black/40 border-white/10 text-white/30 hover:border-white/30 hover:text-white/60'
                }`}
              >
                <AlertTriangle size={20} />
                <span className="font-bold text-xs uppercase tracking-wider">
                  Invalidated ({prediction.votes.invalid.length})
                </span>
              </button>
            )}
          </div>
        )}

        {isAuthor && isInProgress && (
          <div className="pt-6 border-t border-white/10">
            <h4 className="text-[10px] font-bold text-white/30 uppercase tracking-widest mb-4 text-center">
              Resolve Prediction
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <Button
                variant="secondary"
                onClick={() => handleResolve('success')}
                loading={isResolving}
                className="bg-emerald-500/20 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30"
              >
                Success
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleResolve('fail')}
                loading={isResolving}
                className="bg-rose-500/20 border-rose-500/30 text-rose-400 hover:bg-rose-500/30"
              >
                Failed
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleResolve('invalidated')}
                loading={isResolving}
              >
                Invalidate
              </Button>
            </div>

            <div className="flex justify-center gap-3 mt-6">
              <Button
                variant="ghost"
                size="sm"
                icon={<Edit2 size={14} />}
                onClick={() => showToast('Edit feature coming soon', 'info')}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={<Trash2 size={14} />}
                onClick={() => setShowDeleteConfirm(true)}
                className="text-rose-400 hover:text-rose-300"
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Prediction"
        message="Are you sure you want to delete this prediction? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}
